// AutismAi - Multidisciplinary Intervention Dashboard (Phase 1)
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Users, UserPlus, Plus, Trash2, FileText, ClipboardList, Stethoscope, Target, AlertCircle, X, ArrowRight, Pencil, Save, Mail, Activity, Shield, Bell, Check, Copy, Video, FileDown, Sparkles, Printer } from "lucide-react";
import { EvaluationsList } from "./VideoEvaluation";
import { GoalHubModal } from "./GoalHub";
import { buildPremiumHTML, openPrintWindow, exportPremiumPDF, printPremium, downloadPremium } from "./PremiumDoc";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useT } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso, lang = "ar") => { try { return new Date(iso).toLocaleString(lang === "en" ? "en-US" : "ar", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };
const fmtErr = (d) => !d ? "Error" : typeof d === "string" ? d : Array.isArray(d) ? d.map(e=>e?.msg||JSON.stringify(e)).join(" ") : d?.msg || JSON.stringify(d);
const tt = (lang, ar, en) => lang === "en" ? en : ar;

const Btn = ({ children, variant="primary", ...p }) => {
  const s = { primary:"bg-[#2F5233] hover:bg-[#1E3622] text-white", outline:"bg-white hover:bg-[#F0EFEB] text-[#1A201E] border border-[#E5E2DB]", ghost:"hover:bg-[#F0EFEB] text-[#4A5553]", danger:"bg-[#D9534F] hover:bg-[#B73E3A] text-white" };
  return <button {...p} className={`px-4 py-2 rounded-lg font-medium transition disabled:opacity-50 ${s[variant]} ${p.className||""}`}>{children}</button>;
};
const Input = (p) => <input {...p} className={`w-full px-4 py-2.5 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 focus:border-[#2F5233] outline-none ${p.className||""}`}/>;
const Card = ({ children, className="", ...rest }) => <div className={`bg-white rounded-xl border border-[#E5E2DB] p-5 shadow-sm ${className}`} {...rest}>{children}</div>;
const Select = (p) => <select {...p} className={`w-full px-3 py-2.5 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 outline-none ${p.className||""}`}>{p.children}</select>;

const COMM_LEVELS = [
  {v:"verbal",         l_ar:"لفظي",         l_en:"Verbal"},
  {v:"partial_verbal", l_ar:"لفظي جزئي",    l_en:"Partial verbal"},
  {v:"non_verbal",     l_ar:"غير لفظي",     l_en:"Non-verbal"},
  {v:"PECS",           l_ar:"PECS",          l_en:"PECS"},
  {v:"signs",          l_ar:"إشارة",         l_en:"Signs"},
];
const SUPPORT_LEVELS = [
  {v:"level1", l_ar:"المستوى 1 (يحتاج دعم)",  l_en:"Level 1 (needs support)"},
  {v:"level2", l_ar:"المستوى 2 (دعم كبير)",   l_en:"Level 2 (substantial support)"},
  {v:"level3", l_ar:"المستوى 3 (دعم مكثف)",   l_en:"Level 3 (very substantial support)"},
];
const INCLUSION = [
  {v:"general_education",  l_ar:"تعليم عام",        l_en:"General education"},
  {v:"resource_room",      l_ar:"غرفة مصادر",       l_en:"Resource room"},
  {v:"self_contained",     l_ar:"فصل خاص",          l_en:"Self-contained class"},
  {v:"inclusion_partial",  l_ar:"دمج جزئي",         l_en:"Partial inclusion"},
];
const optLabel = (arr, v, lang) => { const x = arr.find(z => z.v === v); return x ? (lang === "en" ? x.l_en : x.l_ar) : v; };

// ============ Cases List ============
export function CasesList() {
  const { lang } = useT();
  const [items, setItems] = useState([]);
  const [invites, setInvites] = useState([]);
  const [meta, setMeta] = useState({ support_categories: [] });
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name:"", age:"", grade_class:"", communication_level:"", support_level:"", inclusion_setting:"", support_categories: [], child_id: "" });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all"); // "all" or a category key
  const [search, setSearch] = useState("");
  const [availableChildren, setAvailableChildren] = useState([]);
  const nav = useNavigate();
  const load = () => {
    axios.get(`${API}/cases`).then(r => setItems(r.data));
    axios.get(`${API}/invitations/me`).then(r => setInvites(r.data));
  };
  useEffect(() => {
    load();
    axios.get(`${API}/cases/meta`).then(r => setMeta(r.data)).catch(() => {});
    axios.get(`${API}/children`).then(r => setAvailableChildren(r.data)).catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form, age: parseInt(form.age) };
      if (!payload.child_id) delete payload.child_id;
      const { data } = await axios.post(`${API}/cases`, payload);
      nav(`/cases/${data.id}`);
    }
    catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const accept = async (token) => {
    try { const { data } = await axios.post(`${API}/invitations/${token}/accept`); nav(`/cases/${data.case_id}`); }
    catch (e) { alert(fmtErr(e.response?.data?.detail)); }
  };
  const decline = async (token) => {
    await axios.post(`${API}/invitations/${token}/decline`); load();
  };

  const pickChild = (childId) => {
    if (!childId) { setForm(f => ({ ...f, child_id: "" })); return; }
    const ch = availableChildren.find(x => x.id === childId);
    if (!ch) return;
    setForm(f => ({ ...f, child_id: childId, name: ch.name, age: String(ch.age) }));
  };

  const toggleCat = (key) => setForm(f => {
    const cats = f.support_categories.includes(key) ? f.support_categories.filter(c => c !== key) : [...f.support_categories, key];
    return { ...f, support_categories: cats };
  });

  return (
    <div className="space-y-6 fade-up" data-testid="cases-list">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold font-heading">{tt(lang, "لوحة الدمج متعدد التخصصات", "Multidisciplinary inclusion dashboard")}</h1>
          <p className="text-[#4A5553] mt-1">{tt(lang, "إدارة حالات الدمج التعليمي لذوي الاحتياجات الخاصة (التوحد، الإعاقة الذهنية، صعوبات التعلم، ADHD، اضطرابات النطق، الإعاقات الحركية والحسية) بفريق متخصصين وخطة تدخل فردية مشتركة.", "Manage inclusion cases for learners with diverse needs (autism, intellectual disability, learning difficulties, ADHD, speech disorders, motor & sensory needs) with a team of specialists and a shared individual plan.")}</p>
        </div>
        <Btn data-testid="new-case-btn" onClick={()=>setShow(true)}><Plus className="inline ms-1" size={16}/>{tt(lang, "حالة جديدة", "New case")}</Btn>
      </div>

      {invites.length > 0 && <Card className="border-2 border-[#E8A317]/40 bg-[#E8A317]/5" data-testid="pending-invites-banner">
        <h2 className="text-lg font-semibold mb-3 font-heading flex items-center gap-2"><Bell className="text-[#E8A317]" size={18}/>{tt(lang, "دعوات في انتظارك", "Invitations awaiting you")} ({invites.length})</h2>
        <div className="space-y-2">{invites.map(inv => <div key={inv.token} className="p-3 bg-white rounded-lg border border-[#E5E2DB] flex justify-between items-center flex-wrap gap-3" data-testid={`invite-${inv.token}`}>
          <div>
            <p className="text-sm">{tt(lang, <><strong>{inv.invited_by_name}</strong> دعاك للانضمام كـ <strong>{inv.role_label}</strong> في حالة <strong>{inv.case_preview?.name}</strong> ({inv.case_preview?.age} سنة)</>, <><strong>{inv.invited_by_name}</strong> invited you to join as <strong>{inv.role_label}</strong> in the case <strong>{inv.case_preview?.name}</strong> ({inv.case_preview?.age} yrs)</>)}</p>
            <p className="text-xs text-[#4A5553] mt-1">{tt(lang, "صالحة حتى", "Valid until")}: {fmtDate(inv.expires_at, lang)}</p>
          </div>
          <div className="flex gap-2">
            <Btn onClick={()=>accept(inv.token)} data-testid={`accept-${inv.token}`}><Check size={14} className="inline ms-1"/>{tt(lang, "قبول", "Accept")}</Btn>
            <Btn variant="outline" onClick={()=>decline(inv.token)} data-testid={`decline-${inv.token}`}>{tt(lang, "رفض", "Decline")}</Btn>
          </div>
        </div>)}</div>
      </Card>}

      {show && <Card data-testid="case-form-card">
        <div className="flex justify-between items-center mb-4"><h2 className="text-xl font-semibold font-heading">{tt(lang, "إنشاء حالة جديدة", "Create a new case")}</h2>
          <button onClick={()=>setShow(false)}><X size={20}/></button></div>
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="case-form">
          {availableChildren.length > 0 && <div className="md:col-span-2 p-3 bg-[#769FCD]/10 rounded-lg border border-[#769FCD]/30">
            <label className="text-xs text-[#527598] block mb-2 font-semibold flex items-center gap-1"><Link to="#" className="pointer-events-none"><span className="inline-block w-2 h-2 rounded-full bg-[#769FCD]"/></Link>{tt(lang, "ربط بطفل موجود (موصى به)", "Link to an existing child (recommended)")}</label>
            <Select data-testid="cf-child-link" value={form.child_id} onChange={e=>pickChild(e.target.value)}>
              <option value="">{tt(lang, "— حالة بدون ربط بطفل —", "— Case without child link —")}</option>
              {availableChildren.map(ch => <option key={ch.id} value={ch.id}>{ch.name} ({ch.age} {tt(lang, "سنة", "yrs")})</option>)}
            </Select>
            {form.child_id && <p className="text-[11px] text-[#527598] mt-1.5">{tt(lang, "سيتم استخدام بيانات الطفل المركزية. الاسم والعمر يُعبّآن تلقائياً.", "Central child data will be used. Name and age auto-fill.")}</p>}
          </div>}
          <Input placeholder={tt(lang, "اسم الطفل", "Child's name")} required data-testid="cf-name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} disabled={!!form.child_id}/>
          <Input type="number" min={1} max={25} placeholder={tt(lang, "العمر", "Age")} required data-testid="cf-age" value={form.age} onChange={e=>setForm({...form,age:e.target.value})} disabled={!!form.child_id}/>
          <Input placeholder={tt(lang, "الصف / المرحلة", "Grade / stage")} data-testid="cf-grade" value={form.grade_class} onChange={e=>setForm({...form,grade_class:e.target.value})}/>
          <Select data-testid="cf-comm" value={form.communication_level} onChange={e=>setForm({...form,communication_level:e.target.value})}>
            <option value="">{tt(lang, "مستوى التواصل", "Communication level")}</option>{COMM_LEVELS.map(o=><option key={o.v} value={o.v}>{lang === "en" ? o.l_en : o.l_ar}</option>)}</Select>
          <Select data-testid="cf-support" value={form.support_level} onChange={e=>setForm({...form,support_level:e.target.value})}>
            <option value="">{tt(lang, "مستوى الدعم", "Support level")}</option>{SUPPORT_LEVELS.map(o=><option key={o.v} value={o.v}>{lang === "en" ? o.l_en : o.l_ar}</option>)}</Select>
          <Select data-testid="cf-inclusion" value={form.inclusion_setting} onChange={e=>setForm({...form,inclusion_setting:e.target.value})}>
            <option value="">{tt(lang, "إطار الدمج", "Inclusion setting")}</option>{INCLUSION.map(o=><option key={o.v} value={o.v}>{lang === "en" ? o.l_en : o.l_ar}</option>)}</Select>
          <div className="md:col-span-2">
            <label className="text-xs text-[#4A5553] block mb-2">{tt(lang, "فئات الدعم (اختر كل ما ينطبق على هذا الطفل)", "Support categories (select all that apply)")}</label>
            <div className="flex flex-wrap gap-2" data-testid="cf-categories">
              {meta.support_categories.map(c => {
                const active = form.support_categories.includes(c.key);
                return <button key={c.key} type="button" onClick={()=>toggleCat(c.key)} data-testid={`cf-cat-${c.key}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition ${active?"bg-[#2F5233] text-white border-[#2F5233]":"bg-white text-[#4A5553] border-[#E5E2DB] hover:border-[#769FCD]"}`}>
                  {lang === "en" && c.label_en ? c.label_en : c.label_ar}
                </button>;
              })}
            </div>
          </div>
          <div className="md:col-span-2 flex gap-3">
            <Btn type="submit" disabled={busy} data-testid="cf-save">{busy?"...":tt(lang, "حفظ وفتح الحالة", "Save & open case")}</Btn>
            <Btn variant="outline" type="button" onClick={()=>setShow(false)}>{tt(lang, "إلغاء", "Cancel")}</Btn>
          </div>
        </form>
      </Card>}

      {items.length === 0 ? <Card><p className="text-center text-[#4A5553] py-6">{tt(lang, "لا توجد حالات بعد. ابدأ بإنشاء حالة جديدة.", "No cases yet. Start by creating a new case.")}</p></Card> : <>
        {/* Filter chips + search */}
        <Card className="!p-3">
          <div className="flex items-center gap-2 flex-wrap" data-testid="cases-filters">
            <input type="search" placeholder={tt(lang, "ابحث باسم الطفل أو الصف...", "Search by child's name or grade...")} value={search} onChange={e=>setSearch(e.target.value)} className="px-3 py-1.5 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 outline-none text-sm flex-1 min-w-[180px]" data-testid="cases-search"/>
            <button onClick={()=>setFilter("all")} data-testid="filter-all" className={`px-3 py-1.5 rounded-full text-xs border transition ${filter==="all"?"bg-[#2F5233] text-white border-[#2F5233]":"bg-white text-[#4A5553] border-[#E5E2DB] hover:border-[#769FCD]"}`}>{tt(lang, "الكل", "All")} ({items.length})</button>
            {meta.support_categories.map(c => {
              const count = items.filter(it => (it.support_categories || []).includes(c.key)).length;
              if (count === 0) return null;
              const active = filter === c.key;
              return <button key={c.key} onClick={()=>setFilter(c.key)} data-testid={`filter-${c.key}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition ${active?"bg-[#2F5233] text-white border-[#2F5233]":"bg-white text-[#4A5553] border-[#E5E2DB] hover:border-[#769FCD]"}`}>
                {lang === "en" && c.label_en ? c.label_en : c.label_ar} ({count})
              </button>;
            })}
          </div>
        </Card>

        {(() => {
          const q = search.trim().toLowerCase();
          const filtered = items.filter(c => {
            if (filter !== "all" && !((c.support_categories || []).includes(filter))) return false;
            if (q) {
              const hay = `${c.name||""} ${c.grade_class||""}`.toLowerCase();
              if (!hay.includes(q)) return false;
            }
            return true;
          });
          if (filtered.length === 0) return <Card><p className="text-center text-[#4A5553] py-6 text-sm">لا توجد حالات تطابق الفلتر الحالي.</p></Card>;
          return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="filtered-cases">{filtered.map(c =>
            <Link key={c.id} to={`/cases/${c.id}`} data-testid={`case-card-${c.id}`}>
              <Card className="hover:border-[#2F5233] transition cursor-pointer">
                <div className="flex justify-between items-start"><h3 className="font-semibold">{c.name}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{c.status==="active"?"نشطة":"مؤرشفة"}</span></div>
                <p className="text-xs text-[#4A5553] mt-1">{c.age} سنة · {c.grade_class || "—"}</p>
                {(c.support_categories || []).length > 0 && <div className="flex flex-wrap gap-1 mt-2">
                  {c.support_categories.slice(0,3).map(k => {
                    const m = meta.support_categories.find(x => x.key === k);
                    return <span key={k} className="text-[10px] px-1.5 py-0.5 bg-[#2F5233]/10 text-[#2F5233] rounded">{m?.label_ar || k}</span>;
                  })}
                  {c.support_categories.length > 3 && <span className="text-[10px] text-[#4A5553]">+{c.support_categories.length - 3}</span>}
                </div>}
                <div className="flex flex-wrap gap-1 mt-3">
                  <span className="text-xs px-2 py-0.5 bg-[#F0EFEB] rounded">{c.team?.length || 0} متخصصين</span>
                  <span className="text-xs px-2 py-0.5 bg-[#769FCD]/15 text-[#527598] rounded">{Object.values(c.plan||{}).reduce((s,g)=>s+(g?.length||0),0)} هدف</span>
                  {c.my_role === "member" ? <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">عضو</span>
                    : <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">منسق</span>}
                </div>
              </Card>
            </Link>
          )}</div>;
        })()}
      </>}
    </div>
  );
}

// ============ Case Detail (tabs) ============
const TABS = [
  { key:"dashboard",   label_ar:"اللوحة",         label_en:"Dashboard",          icon: ClipboardList },
  { key:"profile",     label_ar:"الملف",          label_en:"Profile",            icon: FileText },
  { key:"team",        label_ar:"الفريق",         label_en:"Team",               icon: Users },
  { key:"plan",        label_ar:"خطة التدخل",     label_en:"Intervention plan",  icon: Target },
  { key:"notes",       label_ar:"الملاحظات",      label_en:"Notes",              icon: Stethoscope },
  { key:"evaluations", label_ar:"تقييم الفيديو",  label_en:"Video evaluations",  icon: Video },
  { key:"activity",    label_ar:"سجل الأنشطة",    label_en:"Activity log",       icon: Activity },
];

// ============ Linked Child Banner (Professional Linking System v3.0) ============
function LinkedChildBanner({ c, onChange, isOwner }) {
  const [showLink, setShowLink] = useState(false);
  const [children, setChildren] = useState([]);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);

  const loadChildren = () => {
    axios.get(`${API}/children`).then(r => setChildren(r.data)).catch(() => {});
  };

  const openLink = () => { loadChildren(); setShowLink(true); };

  const linkNow = async () => {
    if (!picked) return;
    setBusy(true);
    try { await axios.post(`${API}/cases/${c.id}/link-child`, { child_id: picked }); onChange(); setShowLink(false); setPicked(""); }
    catch (e) { alert(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const unlink = async () => {
    if (!window.confirm("فك ربط هذه الحالة من الطفل المركزي؟")) return;
    setBusy(true);
    try { await axios.post(`${API}/cases/${c.id}/link-child`, { child_id: null }); onChange(); }
    catch (e) { alert(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (c.linked_child) {
    const ch = c.linked_child;
    return <div className="rounded-xl border-2 border-[#2F5233]/30 bg-gradient-to-r from-[#2F5233]/5 to-[#769FCD]/5 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="linked-child-banner">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-full bg-[#2F5233] text-white flex items-center justify-center flex-shrink-0 font-bold">{(ch.name || "?").charAt(0)}</div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-[#4A5553]">مرتبطة بالطفل المركزي · النظام الاحترافي</p>
          <p className="text-sm font-semibold truncate">{ch.name} <span className="text-xs font-normal text-[#4A5553]">— {ch.age} سنة · {ch.gender}</span></p>
        </div>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <Link to={`/children/${ch.id}`}><Btn variant="outline" data-testid="open-child-btn"><ArrowRight size={14} className="inline ms-1"/>فتح ملف الطفل</Btn></Link>
        {isOwner && <Btn variant="ghost" onClick={unlink} disabled={busy} data-testid="unlink-child-btn"><X size={14} className="inline ms-1"/>فك الربط</Btn>}
      </div>
    </div>;
  }

  if (!isOwner) return null;
  return <div className="rounded-xl border-2 border-dashed border-[#E8A317]/40 bg-[#E8A317]/5 p-3" data-testid="unlinked-banner">
    {!showLink ? <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <AlertCircle size={18} className="text-[#E8A317]"/>
        <span className="text-sm">هذه الحالة غير مرتبطة بطفل في السجل المركزي. ننصح بالربط للحصول على نظام مترابط.</span>
      </div>
      <Btn variant="outline" onClick={openLink} data-testid="link-child-btn"><Plus size={14} className="inline ms-1"/>ربط بطفل</Btn>
    </div> : <div className="space-y-3">
      <p className="text-sm font-medium">اختر الطفل من السجل المركزي</p>
      {children.length === 0 ? <p className="text-xs text-[#4A5553]">لا يوجد أطفال مسجّلون. أضف طفلاً من صفحة <Link to="/children" className="underline text-[#2F5233]">الأطفال</Link> أولاً.</p> :
      <div className="flex gap-2 flex-wrap items-center">
        <Select value={picked} onChange={e=>setPicked(e.target.value)} className="flex-1 min-w-[200px]" data-testid="pick-child-select">
          <option value="">— اختر طفلاً —</option>
          {children.map(ch => <option key={ch.id} value={ch.id}>{ch.name} ({ch.age} سنة)</option>)}
        </Select>
        <Btn onClick={linkNow} disabled={!picked || busy} data-testid="confirm-link-btn"><Save size={14} className="inline ms-1"/>{busy?"...":"ربط"}</Btn>
        <Btn variant="outline" onClick={()=>{ setShowLink(false); setPicked(""); }}>إلغاء</Btn>
      </div>}
    </div>}
  </div>;
}

export function CaseDetail() {
  const { cid } = useParams();
  const { lang } = useT();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [meta, setMeta] = useState({ roles: [], goal_domains: [] });

  const load = () => axios.get(`${API}/cases/${cid}`).then(r => setC(r.data));
  useEffect(() => {
    load();
    axios.get(`${API}/cases/meta`).then(r => setMeta(r.data));
  }, [cid]); // eslint-disable-line

  if (!c) return <div className="p-6 text-[#4A5553]">جاري التحميل...</div>;

  return (
    <div className="space-y-6 fade-up" data-testid="case-detail">
      <div>
        <Link to="/cases" className="text-sm text-[#4A5553] hover:text-[#2F5233] inline-flex items-center gap-1"><ArrowRight size={14}/>العودة للحالات</Link>
        <h1 className="text-3xl font-bold font-heading mt-2">{c.name}</h1>
        <p className="text-sm text-[#4A5553] mt-1">{c.age} سنة · {c.grade_class || "—"} · {COMM_LEVELS.find(x=>x.v===c.communication_level)?.l || ""} · {SUPPORT_LEVELS.find(x=>x.v===c.support_level)?.l || ""}</p>
      </div>

      <LinkedChildBanner c={c} onChange={load} isOwner={c.my_role === "owner"}/>

      <div className="flex gap-2 flex-wrap border-b border-[#E5E2DB] pb-2" data-testid="case-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return <button key={t.key} data-testid={`tab-${t.key}`} onClick={()=>setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm transition flex items-center gap-2 ${tab===t.key?"bg-[#2F5233] text-white":"hover:bg-[#F0EFEB] text-[#4A5553]"}`}>
            <Icon size={16}/>{lang === "en" ? t.label_en : t.label_ar}</button>;
        })}
      </div>

      {tab==="dashboard" && <CoordinatorDashboard cid={cid}/>}
      {tab==="profile" && <ProfileSection c={c} onSaved={load}/>}
      {tab==="team" && <TeamSection cid={cid} team={c.team || []} roles={meta.roles} onChange={load} isOwner={c.my_role === "owner"}/>}
      {tab==="plan" && <PlanSection cid={cid} plan={c.plan || {}} domains={meta.goal_domains} onSaved={load} isOwner={c.my_role === "owner"}/>}
      {tab==="notes" && <NotesSection cid={cid} roles={meta.roles}/>}
      {tab==="evaluations" && <EvaluationsList cid={cid}/>}
      {tab==="activity" && <ActivitySection cid={cid}/>}
    </div>
  );
}

// ===== Profile section (edit) — Full Inclusive Disability Profile (v3.1) =====
function ProfileSection({ c, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    name: c.name, age: c.age, grade_class: c.grade_class||"",
    communication_level: c.communication_level||"", support_level: c.support_level||"",
    inclusion_setting: c.inclusion_setting||"",
    strengths: (c.strengths||[]).join(", "),
    interests: (c.interests||[]).join(", "),
    // Sensory
    sensory_visual: c.sensory_profile?.visual || "",
    sensory_auditory: c.sensory_profile?.auditory || "",
    sensory_tactile: c.sensory_profile?.tactile || "",
    sensory_vestibular: c.sensory_profile?.vestibular || "",
    // Motor & access
    motor_gross: c.motor_access_profile?.gross_motor || "",
    motor_fine: c.motor_access_profile?.fine_motor || "",
    motor_mobility: c.motor_access_profile?.mobility_aids || "",
    // Cognitive / academic
    cog_attention: c.cognitive_academic_profile?.attention || "",
    cog_memory: c.cognitive_academic_profile?.memory || "",
    cog_academic: c.cognitive_academic_profile?.academic_levels || "",
    // Behavior / regulation
    beh_self_reg: c.behavior_regulation_profile?.self_reg || "",
    beh_triggers: c.behavior_regulation_profile?.triggers || "",
    beh_coping: c.behavior_regulation_profile?.coping || "",
    // Assistive tech
    assistive_technology: (c.assistive_technology||[]).join(", "),
  });
  const save = async () => {
    const payload = {
      name: form.name, age: parseInt(form.age), grade_class: form.grade_class,
      communication_level: form.communication_level, support_level: form.support_level,
      inclusion_setting: form.inclusion_setting,
      strengths: form.strengths.split(",").map(s=>s.trim()).filter(Boolean),
      interests: form.interests.split(",").map(s=>s.trim()).filter(Boolean),
      sensory_profile: {
        visual: form.sensory_visual, auditory: form.sensory_auditory,
        tactile: form.sensory_tactile, vestibular: form.sensory_vestibular,
      },
      motor_access_profile: {
        gross_motor: form.motor_gross, fine_motor: form.motor_fine,
        mobility_aids: form.motor_mobility,
      },
      cognitive_academic_profile: {
        attention: form.cog_attention, memory: form.cog_memory,
        academic_levels: form.cog_academic,
      },
      behavior_regulation_profile: {
        self_reg: form.beh_self_reg, triggers: form.beh_triggers,
        coping: form.beh_coping,
      },
      assistive_technology: form.assistive_technology.split(",").map(s=>s.trim()).filter(Boolean),
    };
    try { await axios.patch(`${API}/cases/${c.id}`, payload); setEdit(false); onSaved(); }
    catch (e) { alert(fmtErr(e.response?.data?.detail)); }
  };

  const SectionHead = ({ icon, title, subtitle, color }) => <div className="flex items-center gap-2 border-b border-[#E5E2DB] pb-2 mb-3">
    <span className={`text-lg ${color}`}>{icon}</span>
    <div>
      <h3 className="font-semibold text-sm">{title}</h3>
      {subtitle && <p className="text-[10px] text-[#4A5553]">{subtitle}</p>}
    </div>
  </div>;

  if (!edit) return <Card data-testid="profile-section">
    <div className="flex justify-between items-start mb-4"><h2 className="text-xl font-semibold font-heading">ملف الحالة الشامل</h2>
      <Btn variant="outline" onClick={()=>setEdit(true)} data-testid="edit-profile"><Pencil size={14} className="inline ms-1"/>تعديل</Btn></div>

    {/* CORE */}
    <SectionHead icon="●" title="البيانات الأساسية" subtitle="هوية الطفل وإطار الدمج" color="text-[#2F5233]"/>
    <div className="grid md:grid-cols-2 gap-3 text-sm mb-5">
      <Field label="الاسم" value={c.name}/>
      <Field label="العمر" value={`${c.age} سنة`}/>
      <Field label="الصف / المرحلة" value={c.grade_class || "—"}/>
      <Field label="مستوى التواصل" value={COMM_LEVELS.find(x=>x.v===c.communication_level)?.l || "—"}/>
      <Field label="مستوى الدعم" value={SUPPORT_LEVELS.find(x=>x.v===c.support_level)?.l || "—"}/>
      <Field label="إطار الدمج" value={INCLUSION.find(x=>x.v===c.inclusion_setting)?.l || "—"}/>
      <Field label="نقاط القوة" value={(c.strengths||[]).join("، ") || "—"} full/>
      <Field label="الاهتمامات" value={(c.interests||[]).join("، ") || "—"} full/>
    </div>

    {/* SENSORY */}
    <SectionHead icon="◉" title="الملف الحسّي" subtitle="مدخلات بصرية وسمعية ولمسية وتنظيم دهليزي" color="text-[#769FCD]"/>
    <div className="grid md:grid-cols-2 gap-3 text-sm mb-5">
      <Field label="حسي - بصري" value={c.sensory_profile?.visual || "—"}/>
      <Field label="حسي - سمعي" value={c.sensory_profile?.auditory || "—"}/>
      <Field label="حسي - لمسي" value={c.sensory_profile?.tactile || "—"}/>
      <Field label="حسي - دهليزي / توازن" value={c.sensory_profile?.vestibular || "—"}/>
    </div>

    {/* MOTOR & ACCESS */}
    <SectionHead icon="↻" title="الملف الحركي والوصول" subtitle="المهارات الحركية وأدوات التنقل" color="text-[#B8704A]"/>
    <div className="grid md:grid-cols-2 gap-3 text-sm mb-5">
      <Field label="مهارات حركية كبرى (gross)" value={c.motor_access_profile?.gross_motor || "—"}/>
      <Field label="مهارات حركية دقيقة (fine)" value={c.motor_access_profile?.fine_motor || "—"}/>
      <Field label="أدوات التنقل / المساعِدة" value={c.motor_access_profile?.mobility_aids || "—"} full/>
    </div>

    {/* COGNITIVE / ACADEMIC */}
    <SectionHead icon="◆" title="الملف المعرفي / الأكاديمي" subtitle="الانتباه والذاكرة والمستويات الدراسية" color="text-[#5E8A6E]"/>
    <div className="grid md:grid-cols-2 gap-3 text-sm mb-5">
      <Field label="الانتباه" value={c.cognitive_academic_profile?.attention || "—"}/>
      <Field label="الذاكرة" value={c.cognitive_academic_profile?.memory || "—"}/>
      <Field label="المستويات الأكاديمية" value={c.cognitive_academic_profile?.academic_levels || "—"} full/>
    </div>

    {/* BEHAVIOR / REGULATION */}
    <SectionHead icon="◯" title="السلوك والتنظيم الذاتي" subtitle="التنظيم العاطفي ومحفّزات السلوك واستراتيجيات التهدئة" color="text-[#A67BA1]"/>
    <div className="grid md:grid-cols-2 gap-3 text-sm mb-5">
      <Field label="التنظيم الذاتي" value={c.behavior_regulation_profile?.self_reg || "—"}/>
      <Field label="المحفّزات (Triggers)" value={c.behavior_regulation_profile?.triggers || "—"}/>
      <Field label="استراتيجيات التهدئة (Coping)" value={c.behavior_regulation_profile?.coping || "—"} full/>
    </div>

    {/* ASSISTIVE TECH */}
    <SectionHead icon="⚙" title="التقنية المساعِدة" subtitle="AAC · مفاتيح · قارئ شاشة · أدوات تكيّف" color="text-[#E8A317]"/>
    <div className="text-sm">
      {(c.assistive_technology||[]).length === 0 ? <p className="text-[#4A5553]">لا توجد أدوات مسجّلة</p> :
        <div className="flex flex-wrap gap-2">{(c.assistive_technology||[]).map((t,i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#E8A317]/15 text-[#A36F00] border border-[#E8A317]/30">{t}</span>)}</div>}
    </div>
  </Card>;

  return <Card data-testid="profile-edit-form">
    <div className="flex justify-between items-start mb-4"><h2 className="text-xl font-semibold font-heading">تعديل ملف الحالة الشامل</h2></div>

    <SectionHead icon="●" title="البيانات الأساسية" color="text-[#2F5233]"/>
    <div className="grid md:grid-cols-2 gap-3 mb-5">
      <Input placeholder="الاسم" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="pf-name"/>
      <Input type="number" placeholder="العمر" value={form.age} onChange={e=>setForm({...form,age:e.target.value})} data-testid="pf-age"/>
      <Input placeholder="الصف / المرحلة" value={form.grade_class} onChange={e=>setForm({...form,grade_class:e.target.value})}/>
      <Select value={form.communication_level} onChange={e=>setForm({...form,communication_level:e.target.value})}>
        <option value="">مستوى التواصل</option>{COMM_LEVELS.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}</Select>
      <Select value={form.support_level} onChange={e=>setForm({...form,support_level:e.target.value})}>
        <option value="">مستوى الدعم</option>{SUPPORT_LEVELS.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}</Select>
      <Select value={form.inclusion_setting} onChange={e=>setForm({...form,inclusion_setting:e.target.value})}>
        <option value="">إطار الدمج</option>{INCLUSION.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}</Select>
      <Input placeholder="نقاط القوة (مفصولة بفواصل)" value={form.strengths} onChange={e=>setForm({...form,strengths:e.target.value})} className="md:col-span-2"/>
      <Input placeholder="الاهتمامات (مفصولة بفواصل)" value={form.interests} onChange={e=>setForm({...form,interests:e.target.value})} className="md:col-span-2"/>
    </div>

    <SectionHead icon="◉" title="الملف الحسّي" color="text-[#769FCD]"/>
    <div className="grid md:grid-cols-2 gap-3 mb-5">
      <Input placeholder="حسي - بصري (مثال: حساسية للأضواء الفلورسنت)" value={form.sensory_visual} onChange={e=>setForm({...form,sensory_visual:e.target.value})}/>
      <Input placeholder="حسي - سمعي" value={form.sensory_auditory} onChange={e=>setForm({...form,sensory_auditory:e.target.value})}/>
      <Input placeholder="حسي - لمسي" value={form.sensory_tactile} onChange={e=>setForm({...form,sensory_tactile:e.target.value})}/>
      <Input placeholder="حسي - دهليزي/توازن" value={form.sensory_vestibular} onChange={e=>setForm({...form,sensory_vestibular:e.target.value})} data-testid="pf-vestibular"/>
    </div>

    <SectionHead icon="↻" title="الحركي والوصول" color="text-[#B8704A]"/>
    <div className="grid md:grid-cols-2 gap-3 mb-5">
      <Input placeholder="مهارات حركية كبرى" value={form.motor_gross} onChange={e=>setForm({...form,motor_gross:e.target.value})} data-testid="pf-motor-gross"/>
      <Input placeholder="مهارات حركية دقيقة" value={form.motor_fine} onChange={e=>setForm({...form,motor_fine:e.target.value})} data-testid="pf-motor-fine"/>
      <Input placeholder="أدوات تنقل (كرسي متحرك، عصا، إلخ)" value={form.motor_mobility} onChange={e=>setForm({...form,motor_mobility:e.target.value})} className="md:col-span-2" data-testid="pf-motor-mobility"/>
    </div>

    <SectionHead icon="◆" title="المعرفي / الأكاديمي" color="text-[#5E8A6E]"/>
    <div className="grid md:grid-cols-2 gap-3 mb-5">
      <Input placeholder="الانتباه (مدة التركيز، التشتت)" value={form.cog_attention} onChange={e=>setForm({...form,cog_attention:e.target.value})} data-testid="pf-cog-attention"/>
      <Input placeholder="الذاكرة (قصيرة/طويلة المدى)" value={form.cog_memory} onChange={e=>setForm({...form,cog_memory:e.target.value})} data-testid="pf-cog-memory"/>
      <Input placeholder="المستويات الأكاديمية (قراءة:1، كتابة:1، رياضيات:2)" value={form.cog_academic} onChange={e=>setForm({...form,cog_academic:e.target.value})} className="md:col-span-2" data-testid="pf-cog-academic"/>
    </div>

    <SectionHead icon="◯" title="السلوك والتنظيم الذاتي" color="text-[#A67BA1]"/>
    <div className="grid md:grid-cols-2 gap-3 mb-5">
      <Input placeholder="التنظيم الذاتي" value={form.beh_self_reg} onChange={e=>setForm({...form,beh_self_reg:e.target.value})} data-testid="pf-beh-self-reg"/>
      <Input placeholder="المحفّزات / Triggers" value={form.beh_triggers} onChange={e=>setForm({...form,beh_triggers:e.target.value})} data-testid="pf-beh-triggers"/>
      <Input placeholder="استراتيجيات التهدئة / Coping" value={form.beh_coping} onChange={e=>setForm({...form,beh_coping:e.target.value})} className="md:col-span-2" data-testid="pf-beh-coping"/>
    </div>

    <SectionHead icon="⚙" title="التقنية المساعِدة" color="text-[#E8A317]"/>
    <div className="mb-4">
      <Input placeholder="AAC، كرسي متحرك، قارئ شاشة، لوحة Picture Exchange (مفصولة بفواصل)" value={form.assistive_technology} onChange={e=>setForm({...form,assistive_technology:e.target.value})} data-testid="pf-assistive-tech"/>
      <p className="text-[10px] text-[#4A5553] mt-1">اكتب الأدوات مفصولة بفواصل (،). ستظهر كشارات في عرض الملف.</p>
    </div>

    <div className="flex gap-2 mt-5 pt-3 border-t border-[#E5E2DB]"><Btn onClick={save} data-testid="save-profile"><Save size={14} className="inline ms-1"/>حفظ كل التغييرات</Btn>
      <Btn variant="outline" onClick={()=>setEdit(false)}>إلغاء</Btn></div>
  </Card>;
}

const Field = ({ label, value, full }) => <div className={full?"md:col-span-2":""}><p className="text-xs text-[#4A5553] mb-1">{label}</p><p className="font-medium">{value}</p></div>;

// ===== Team section =====
function TeamSection({ cid, team, roles, onChange, isOwner }) {
  const [showMember, setShowMember] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [memberForm, setMemberForm] = useState({ name:"", role:"", contact:"" });
  const [inviteForm, setInviteForm] = useState({ email:"", role:"", days_valid:14 });
  const [invites, setInvites] = useState([]);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadInvites = () => isOwner && axios.get(`${API}/cases/${cid}/invites`).then(r => setInvites(r.data));
  useEffect(() => { loadInvites(); }, [cid, isOwner]); // eslint-disable-line

  const addMember = async (e) => { e.preventDefault();
    try { await axios.post(`${API}/cases/${cid}/team`, memberForm); setMemberForm({name:"",role:"",contact:""}); setShowMember(false); onChange(); }
    catch (err) { alert(fmtErr(err.response?.data?.detail)); }
  };
  const sendInvite = async (e) => { e.preventDefault();
    try { const { data } = await axios.post(`${API}/cases/${cid}/invites`, inviteForm);
      const url = `${window.location.origin}/invitations`;
      setGeneratedLink({ url, email: inviteForm.email, role_label: data.role_label });
      setInviteForm({ email:"", role:"", days_valid:14 });
      loadInvites();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); }
  };
  const remove = async (mid) => { if(!window.confirm("إزالة عضو الفريق؟")) return; await axios.delete(`${API}/cases/${cid}/team/${mid}`); onChange(); };
  const revokeInvite = async (token) => { await axios.delete(`${API}/cases/invites/${token}`); loadInvites(); };
  const copyLink = async () => { await navigator.clipboard.writeText(generatedLink.url); setCopied(true); setTimeout(()=>setCopied(false),2000); };

  const pendingInvites = invites.filter(i => i.status === "pending");

  return <Card data-testid="team-section">
    <div className="flex justify-between items-center mb-4 flex-wrap gap-2"><h2 className="text-xl font-semibold font-heading">فريق التدخل</h2>
      {isOwner && <div className="flex gap-2">
        <Btn variant="outline" onClick={()=>setShowInvite(true)} data-testid="invite-specialist-btn"><Mail size={14} className="inline ms-1"/>دعوة عبر البريد</Btn>
        <Btn variant="outline" onClick={()=>setShowMember(true)} data-testid="add-member-btn"><UserPlus size={14} className="inline ms-1"/>إضافة سريعة</Btn>
      </div>}
    </div>

    {!isOwner && <div className="mb-4 p-3 bg-[#769FCD]/10 rounded-lg flex items-center gap-2 text-sm"><Shield size={14} className="text-[#527598]"/>أنت عضو في الفريق - يمكنك إضافة الملاحظات فقط</div>}

    {showInvite && isOwner && <form onSubmit={sendInvite} className="mb-4 p-4 bg-[#F9F8F6] rounded-lg space-y-3" data-testid="invite-form">
      <p className="text-sm text-[#4A5553]">يتم إنشاء دعوة آمنة. سيرى المتخصص الدعوة عند تسجيل دخوله بنفس البريد.</p>
      <div className="grid md:grid-cols-3 gap-3">
        <Input type="email" placeholder="البريد الإلكتروني للمتخصص" required value={inviteForm.email} onChange={e=>setInviteForm({...inviteForm,email:e.target.value})} data-testid="inv-email"/>
        <Select value={inviteForm.role} onChange={e=>setInviteForm({...inviteForm,role:e.target.value})} required data-testid="inv-role">
          <option value="">اختر الدور</option>{roles.map(r=><option key={r.key} value={r.key}>{r.label_ar}</option>)}</Select>
        <Input type="number" min={1} max={30} placeholder="صلاحية بالأيام" value={inviteForm.days_valid} onChange={e=>setInviteForm({...inviteForm,days_valid:parseInt(e.target.value)||14})} data-testid="inv-days"/>
      </div>
      <div className="flex gap-2"><Btn type="submit" data-testid="inv-send"><Mail size={14} className="inline ms-1"/>إرسال الدعوة</Btn>
        <Btn variant="outline" type="button" onClick={()=>{setShowInvite(false); setGeneratedLink(null);}}>إغلاق</Btn></div>
      {generatedLink && <div className="mt-3 p-3 bg-white rounded-lg border border-[#3A7D44]" data-testid="invite-result">
        <p className="text-sm text-[#3A7D44] font-medium"><Check size={14} className="inline ms-1"/>أُرسلت الدعوة إلى {generatedLink.email} كـ {generatedLink.role_label}</p>
        <p className="text-xs text-[#4A5553] mt-2">شارك معه الرابط (سيرى الدعوة فور تسجيل دخوله):</p>
        <div className="flex gap-2 mt-1">
          <code className="text-xs bg-[#F0EFEB] px-2 py-1 rounded flex-1 break-all">{generatedLink.url}</code>
          <Btn variant="outline" onClick={copyLink} data-testid="inv-copy">{copied?<><Check size={12} className="inline ms-1"/>تم</>:<><Copy size={12} className="inline ms-1"/>نسخ</>}</Btn>
        </div>
      </div>}
    </form>}

    {showMember && isOwner && <form onSubmit={addMember} className="mb-4 p-4 bg-[#F9F8F6] rounded-lg grid md:grid-cols-3 gap-3" data-testid="team-form">
      <Input placeholder="اسم المتخصص" required value={memberForm.name} onChange={e=>setMemberForm({...memberForm,name:e.target.value})} data-testid="tm-name"/>
      <Select value={memberForm.role} onChange={e=>setMemberForm({...memberForm,role:e.target.value})} required data-testid="tm-role">
        <option value="">اختر الدور</option>{roles.map(r=><option key={r.key} value={r.key}>{r.label_ar}</option>)}</Select>
      <Input placeholder="بريد / هاتف (اختياري)" value={memberForm.contact} onChange={e=>setMemberForm({...memberForm,contact:e.target.value})} data-testid="tm-contact"/>
      <div className="md:col-span-3 flex gap-2"><Btn type="submit" data-testid="tm-save">إضافة</Btn><Btn variant="outline" type="button" onClick={()=>setShowMember(false)}>إلغاء</Btn></div>
    </form>}

    {team.length === 0 ? <p className="text-center text-[#4A5553] py-4 text-sm">لا يوجد متخصصين بعد</p> :
      <div className="grid md:grid-cols-2 gap-3">{team.map(m => <div key={m.id} className="p-3 border border-[#E5E2DB] rounded-lg flex justify-between items-center" data-testid={`member-${m.id}`}>
        <div><div className="flex items-center gap-2"><p className="font-semibold">{m.name}</p>
          {m.user_id && <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full" data-testid={`verified-${m.id}`}>متحقّق</span>}</div>
          <p className="text-xs text-[#4A5553] mt-0.5">{m.role_label}</p>
          {m.contact && <p className="text-xs text-[#4A5553]">{m.contact}</p>}</div>
        {isOwner && <button onClick={()=>remove(m.id)} className="p-2 hover:bg-red-50 rounded text-[#D9534F]" data-testid={`remove-${m.id}`}><Trash2 size={16}/></button>}
      </div>)}</div>}

    {isOwner && pendingInvites.length > 0 && <div className="mt-5" data-testid="pending-invites-section">
      <h3 className="text-sm font-semibold mb-2 flex items-center gap-1"><Mail size={14}/>دعوات معلّقة ({pendingInvites.length})</h3>
      <div className="space-y-2">{pendingInvites.map(inv => <div key={inv.token} className="p-3 bg-[#F9F8F6] rounded-lg flex justify-between items-center" data-testid={`inv-row-${inv.token}`}>
        <div><p className="text-sm">{inv.email}</p><p className="text-xs text-[#4A5553]">{inv.role_label} · صالحة حتى {fmtDate(inv.expires_at)}</p></div>
        <button onClick={()=>revokeInvite(inv.token)} className="p-2 hover:bg-red-50 rounded text-[#D9534F]" title="إلغاء الدعوة"><Trash2 size={14}/></button>
      </div>)}</div>
    </div>}
  </Card>;
}

// ===== Plan section (read-only for members) =====
function PlanSection({ cid, plan, domains, onSaved, isOwner }) {
  const [localPlan, setLocalPlan] = useState(() => {
    const init = {};
    domains.forEach(d => { init[d.key] = plan[d.key] || []; });
    return init;
  });
  const [evalsByGoal, setEvalsByGoal] = useState({}); // goal_id -> [{id, title, comparison_summary}]
  const [hubGoal, setHubGoal] = useState(null); // {goal, domainLabel}
  const [me, setMe] = useState(null);
  useEffect(() => { axios.get(`${API}/auth/me`).then(r => setMe(r.data)).catch(()=>{}); }, []);
  useEffect(() => {
    const init = {};
    domains.forEach(d => { init[d.key] = plan[d.key] || []; });
    setLocalPlan(init);
  }, [plan, domains]);
  useEffect(() => {
    axios.get(`${API}/cases/${cid}/evaluations`).then(r => {
      const map = {};
      (r.data || []).forEach(ev => {
        if (ev.goal_id) {
          map[ev.goal_id] = map[ev.goal_id] || [];
          map[ev.goal_id].push(ev);
        }
      });
      setEvalsByGoal(map);
    }).catch(() => {});
  }, [cid, plan]);
  const [busy, setBusy] = useState(false);

  // ===== Compute automatic goal-achievement score per goal =====
  // Score: avg over all linked evaluations of (Σ final – Σ baseline) / (4 indicators * 5 max) * 100
  // Falls back to evidence-based heuristic if rubric not filled
  const computeGoalScore = (linkedEvals) => {
    if (!linkedEvals.length) return null;
    let totalPct = 0, counted = 0;
    for (const ev of linkedEvals) {
      const scores = ev.rubric_scores || {};
      const inds = Object.keys(scores);
      if (inds.length > 0) {
        let b = 0, f = 0;
        inds.forEach(k => { b += (scores[k]?.baseline || 0); f += (scores[k]?.final || 0); });
        const max = inds.length * 5;
        if (max > 0) {
          // delta / max → 0..1, but allow negative
          const pct = Math.max(0, Math.min(100, Math.round(((f - b) / max) * 100)));
          totalPct += pct; counted++;
        }
      } else {
        // fallback: use evidence counts (each final note worth +1, baseline -0.5, capped at 80%)
        const baseN = (ev.baseline?.notes?.length || 0);
        const finalN = (ev.final?.notes?.length || 0);
        if (baseN + finalN > 0) {
          const pct = Math.max(0, Math.min(80, Math.round(((finalN - baseN * 0.5) / Math.max(baseN + finalN, 4)) * 100)));
          totalPct += pct; counted++;
        }
      }
    }
    if (!counted) return null;
    return Math.round(totalPct / counted);
  };

  const scoreColor = (s) => {
    if (s == null) return "bg-[#F0EFEB] text-[#4A5553]";
    if (s >= 70) return "bg-emerald-100 text-emerald-700";
    if (s >= 40) return "bg-amber-100 text-amber-700";
    return "bg-rose-100 text-rose-700";
  };
  const scoreBarColor = (s) => {
    if (s == null) return "bg-[#E5E2DB]";
    if (s >= 70) return "bg-emerald-500";
    if (s >= 40) return "bg-amber-500";
    return "bg-rose-500";
  };

  const addGoal = (d) => setLocalPlan(p => ({ ...p, [d]: [...(p[d]||[]), { title:"", criteria:"", responsible:"", status:"active" }] }));
  const updateGoal = (d, i, k, v) => setLocalPlan(p => {
    const arr = [...(p[d]||[])]; arr[i] = { ...arr[i], [k]: v };
    return { ...p, [d]: arr };
  });
  const removeGoal = (d, i) => setLocalPlan(p => ({ ...p, [d]: p[d].filter((_,idx)=>idx!==i) }));
  const save = async () => {
    setBusy(true);
    try { await axios.put(`${API}/cases/${cid}/plan`, localPlan); onSaved(); }
    catch (e) { alert(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const [aiBusy, setAiBusy] = useState(false);
  const generateSmart = async () => {
    if (!window.confirm("سيُضيف الذكاء الاصطناعي أهدافاً مقترحة حسب فئات الدعم المختارة للحالة (دمج مع الأهداف الحالية).\n\nمتابعة؟")) return;
    setAiBusy(true);
    try {
      await axios.post(`${API}/cases/${cid}/plan/generate`, { merge: true });
      onSaved();
    } catch (e) { alert(fmtErr(e.response?.data?.detail)); } finally { setAiBusy(false); }
  };

  return <Card data-testid="plan-section">
    <div className="flex justify-between items-center mb-4 flex-wrap gap-2"><h2 className="text-xl font-semibold font-heading">خطة التدخل المشتركة</h2>
      {isOwner ? <div className="flex gap-2 flex-wrap">
        <Btn variant="accent" onClick={generateSmart} disabled={aiBusy} data-testid="ai-generate-plan"><Sparkles size={14} className="inline ms-1"/>{aiBusy?"يولّد...":"توليد خطة ذكية حسب فئات الدعم"}</Btn>
        <Btn onClick={save} disabled={busy} data-testid="save-plan"><Save size={14} className="inline ms-1"/>{busy?"...":"حفظ الخطة"}</Btn>
      </div>
        : <span className="text-xs px-2 py-1 bg-[#769FCD]/15 text-[#527598] rounded flex items-center gap-1"><Shield size={12}/>عرض فقط</span>}</div>
    <p className="text-xs text-[#4A5553] mb-3">يظهر بجانب كل هدف عدد تقييمات الفيديو المرتبطة به وحالة تقرير المقارنة، لمتابعة مدى تحقق الهدف بأدلة موثّقة.</p>
    <div className="space-y-5">{domains.map(d => <div key={d.key} className="border border-[#E5E2DB] rounded-lg p-3" data-testid={`domain-${d.key}`}>
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-semibold text-sm">{d.label_ar} ({(localPlan[d.key]||[]).length})</h3>
        {isOwner && <Btn variant="ghost" onClick={()=>addGoal(d.key)} data-testid={`add-goal-${d.key}`}><Plus size={14} className="inline ms-1"/>هدف</Btn>}
      </div>
      {(localPlan[d.key]||[]).length === 0 ? <p className="text-xs text-[#4A5553] italic">لا توجد أهداف في هذا المجال</p> :
        <div className="space-y-2">{localPlan[d.key].map((g, i) => {
          const linked = g.id ? (evalsByGoal[g.id] || []) : [];
          const withReport = linked.filter(ev => ev.comparison_summary).length;
          const score = computeGoalScore(linked);
          return isOwner ? <div key={g.id || `${d.key}-${i}`} className="p-2 bg-[#F9F8F6] rounded space-y-2" data-testid={`goal-row-${d.key}-${i}`}>
            <div className="grid md:grid-cols-12 gap-2 items-center">
              <div className="md:col-span-4 flex items-center gap-1">
                <Input className="flex-1" placeholder="عنوان الهدف" value={g.title} onChange={e=>updateGoal(d.key,i,"title",e.target.value)} data-testid={`goal-title-${d.key}-${i}`}/>
                {g.ai_generated && <span title="مولّد بالذكاء الاصطناعي" className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded flex items-center gap-0.5"><Sparkles size={9}/>AI</span>}
              </div>
              <Input className="md:col-span-3" placeholder="معيار النجاح" value={g.criteria||""} onChange={e=>updateGoal(d.key,i,"criteria",e.target.value)}/>
              <Input className="md:col-span-2" placeholder="المسؤول" value={g.responsible||""} onChange={e=>updateGoal(d.key,i,"responsible",e.target.value)}/>
              <div className="md:col-span-2 text-xs flex items-center gap-1 flex-wrap">
                {linked.length > 0 ? <>
                  <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{linked.length} تقييم</span>
                  {withReport > 0 && <span className="px-1.5 py-0.5 rounded bg-[#769FCD]/15 text-[#527598]">{withReport} تقرير</span>}
                </> : <span className="text-[#4A5553]">لا تقييمات</span>}
              </div>
              <div className="md:col-span-1 flex gap-1">
                {g.id && g.title && <button onClick={()=>setHubGoal({ goal: g, domainLabel: d.label_ar })} className="p-1.5 hover:bg-[#2F5233]/10 rounded text-[#2F5233]" title="مركز الهدف الذكي" data-testid={`open-hub-${d.key}-${i}`}><Sparkles size={14}/></button>}
                <button onClick={()=>removeGoal(d.key,i)} className="p-1.5 hover:bg-red-50 rounded text-[#D9534F]"><Trash2 size={14}/></button>
              </div>
            </div>
            {linked.length > 0 && <GoalAchievementBar score={score} linkedCount={linked.length} colorCls={scoreColor(score)} barCls={scoreBarColor(score)} testid={`goal-score-${d.key}-${i}`}/>}
          </div> : <div key={g.id || `${d.key}-${i}`} className="p-3 bg-[#F9F8F6] rounded text-sm" data-testid={`goal-row-${d.key}-${i}`}>
            <div className="flex justify-between items-start gap-2 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-medium">{g.title}</p>
                {g.criteria && <p className="text-xs text-[#4A5553] mt-1">المعيار: {g.criteria}</p>}
                {g.responsible && <p className="text-xs text-[#4A5553]">المسؤول: {g.responsible}</p>}
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {g.id && <button onClick={()=>setHubGoal({ goal: g, domainLabel: d.label_ar })} className="px-2 py-1 text-xs bg-[#2F5233]/10 text-[#2F5233] hover:bg-[#2F5233]/20 rounded flex items-center gap-1" data-testid={`open-hub-${d.key}-${i}`}><Sparkles size={11}/>مركز الهدف</button>}
                {linked.length > 0 && <>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{linked.length} تقييم</span>
                  {withReport > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-[#769FCD]/15 text-[#527598]">{withReport} تقرير</span>}
                </>}
              </div>
            </div>
            {linked.length > 0 && <div className="mt-2"><GoalAchievementBar score={score} linkedCount={linked.length} colorCls={scoreColor(score)} barCls={scoreBarColor(score)} testid={`goal-score-${d.key}-${i}`}/></div>}
            {linked.length > 0 && <div className="mt-2 space-y-1">{linked.slice(0,3).map(ev => <Link key={ev.id} to={`/cases/${cid}/evaluations/${ev.id}`} className="block text-xs text-[#527598] hover:underline">→ {ev.title}</Link>)}</div>}
          </div>;
        })}</div>}
    </div>)}</div>
    {hubGoal && <GoalHubModal cid={cid} goal={hubGoal.goal} domain={hubGoal.domainLabel} currentUser={me} onClose={()=>{ setHubGoal(null); onSaved(); }}/>}
  </Card>;
}

// ===== Activity log =====
function ActivitySection({ cid }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/cases/${cid}/activity`).then(r => setItems(r.data)); }, [cid]);
  const labels = {
    profile_updated: "تحديث الملف", member_added: "إضافة عضو", member_removed: "إزالة عضو",
    plan_saved: "حفظ الخطة", note_added: "إضافة ملاحظة", invite_sent: "إرسال دعوة", invite_accepted: "قبول دعوة",
    evaluation_created: "إنشاء تقييم فيديو", evaluation_compared: "إصدار تقرير مقارنة", evaluation_deleted: "حذف تقييم",
  };

  const buildOpts = () => ({
    title: "سجل أنشطة الحالة",
    subtitle: "Activity Audit Log",
    docType: "تقرير تدقيق رسمي",
    subjectName: `حالة رقم ${cid.slice(0,8)}`,
    subjectMeta: { "عدد الأنشطة": items.length, "نطاق التاريخ": items.length>0 ? `${fmtDate(items[items.length-1].ts)} → ${fmtDate(items[0].ts)}` : "—" },
    body: `<div class="section"><h3>التسلسل الزمني للأنشطة</h3>
      <table class="report">
        <thead><tr><th>التاريخ والوقت</th><th>القائم</th><th>الإجراء</th><th>التفاصيل</th></tr></thead>
        <tbody>${items.map(a => `<tr><td>${fmtDate(a.ts)}</td><td>${a.user_name||"—"}</td><td><strong>${labels[a.action]||a.action}</strong></td><td>${a.detail||"—"}</td></tr>`).join("")}</tbody>
      </table></div>`,
    signatures: [{ role: "المنسق المعتمد" }, { role: "مدير قسم الدمج" }],
    refPrefix: "AUD",
  });

  const printLog = () => { if (items.length === 0) { alert("لا توجد أنشطة"); return; } printPremium(buildOpts()); };
  const exportPDF = async () => {
    if (items.length === 0) { alert("لا توجد أنشطة لتصديرها."); return; }
    setBusy(true);
    try { await downloadPremium(buildOpts(), `Activity-Log-${cid.slice(0,8)}.pdf`); }
    catch (err) { console.error("Activity PDF export failed:", err); alert("تعذّر تصدير الـ PDF."); }
    finally { setBusy(false); }
  };

  return <Card data-testid="activity-section">
    <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
      <h2 className="text-xl font-semibold font-heading flex items-center gap-2"><Activity size={20}/>سجل الأنشطة ({items.length})</h2>
      {items.length > 0 && <div className="flex gap-2 flex-wrap">
        <Btn variant="outline" onClick={printLog} data-testid="print-activity"><Printer size={14} className="inline ms-1"/>طباعة</Btn>
        <Btn onClick={exportPDF} disabled={busy} data-testid="export-activity-pdf"><FileDown size={14} className="inline ms-1"/>{busy?"جاري التصدير...":"تنزيل PDF فاخر"}</Btn>
      </div>}
    </div>
    {items.length === 0 ? <p className="text-sm text-[#4A5553] text-center py-4">لا توجد أنشطة بعد</p> :
      <div id="activity-printable" className="bg-white p-3" dir="rtl">
        <div className="text-center mb-4 pb-2 border-b-2 border-[#2F5233]">
          <h3 className="text-lg font-bold font-heading">سجل أنشطة الحالة</h3>
          <p className="text-xs text-[#4A5553] mt-1">رقم الحالة: {cid.slice(0,8)} · {items.length} نشاطاً · صادر بتاريخ {fmtDate(new Date().toISOString())}</p>
        </div>
        <div className="space-y-2">{items.map(a => <div key={a.id} className="p-3 border border-[#E5E2DB] rounded-lg flex justify-between items-center gap-3" data-testid={`activity-${a.id}`}>
          <div><p className="text-sm"><strong>{a.user_name}</strong> · {labels[a.action] || a.action}</p>
            {a.detail && <p className="text-xs text-[#4A5553] mt-1">{a.detail}</p>}</div>
          <span className="text-xs text-[#4A5553] flex-shrink-0">{fmtDate(a.ts)}</span>
        </div>)}</div>
        <p className="text-[10px] text-[#4A5553] mt-4 text-center">— Inclusion+AI · لوحة الدمج متعدد التخصصات —</p>
      </div>}
  </Card>;
}

// ===== Notes section =====
function NotesSection({ cid, roles }) {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ role:"", observation:"", progress_note:"", concern:"", recommendation:"" });
  const load = () => axios.get(`${API}/cases/${cid}/notes`).then(r => setItems(r.data));
  useEffect(() => { load(); }, [cid]); // eslint-disable-line
  const add = async (e) => { e.preventDefault();
    try { await axios.post(`${API}/cases/${cid}/notes`, form);
      setForm({role:"",observation:"",progress_note:"",concern:"",recommendation:""}); setShow(false); load();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); }
  };
  return <Card data-testid="notes-section">
    <div className="flex justify-between items-center mb-4"><h2 className="text-xl font-semibold font-heading">ملاحظات المتخصصين ({items.length})</h2>
      <Btn variant="outline" onClick={()=>setShow(true)} data-testid="add-note-btn"><Plus size={14} className="inline ms-1"/>ملاحظة جديدة</Btn></div>
    {show && <form onSubmit={add} className="mb-5 p-4 bg-[#F9F8F6] rounded-lg space-y-3" data-testid="note-form">
      <Select value={form.role} onChange={e=>setForm({...form,role:e.target.value})} required data-testid="nf-role">
        <option value="">اختر دورك</option>{roles.map(r=><option key={r.key} value={r.key}>{r.label_ar}</option>)}</Select>
      <textarea placeholder="الملاحظة / المشاهدة *" required value={form.observation} onChange={e=>setForm({...form,observation:e.target.value})} data-testid="nf-obs"
        className="w-full px-3 py-2 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 outline-none min-h-[60px]"/>
      <textarea placeholder="ملاحظة التقدم" value={form.progress_note} onChange={e=>setForm({...form,progress_note:e.target.value})} data-testid="nf-prog"
        className="w-full px-3 py-2 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 outline-none min-h-[50px]"/>
      <textarea placeholder="عائق / مخاوف" value={form.concern} onChange={e=>setForm({...form,concern:e.target.value})} data-testid="nf-concern"
        className="w-full px-3 py-2 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 outline-none min-h-[50px]"/>
      <textarea placeholder="توصية" value={form.recommendation} onChange={e=>setForm({...form,recommendation:e.target.value})} data-testid="nf-rec"
        className="w-full px-3 py-2 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 outline-none min-h-[50px]"/>
      <div className="flex gap-2"><Btn type="submit" data-testid="nf-save">حفظ الملاحظة</Btn><Btn variant="outline" type="button" onClick={()=>setShow(false)}>إلغاء</Btn></div>
    </form>}
    {items.length === 0 ? <p className="text-center text-[#4A5553] py-4 text-sm">لا توجد ملاحظات بعد</p> :
      <div className="space-y-3">{items.map(n => <div key={n.id} className="p-3 border border-[#E5E2DB] rounded-lg" data-testid={`note-${n.id}`}>
        <div className="flex justify-between items-start gap-2 flex-wrap">
          <div><span className="text-sm font-semibold">{n.author}</span>
            <span className="text-xs px-2 py-0.5 ms-2 bg-[#769FCD]/15 text-[#527598] rounded-full">{n.role_label}</span></div>
          <span className="text-xs text-[#4A5553]">{fmtDate(n.ts)}</span>
        </div>
        <p className="text-sm mt-2"><strong>الملاحظة:</strong> {n.observation}</p>
        {n.progress_note && <p className="text-sm mt-1 text-[#3A7D44]"><strong>التقدم:</strong> {n.progress_note}</p>}
        {n.concern && <p className="text-sm mt-1 text-[#E8A317]"><strong>عائق:</strong> {n.concern}</p>}
        {n.recommendation && <p className="text-sm mt-1 text-[#527598]"><strong>توصية:</strong> {n.recommendation}</p>}
      </div>)}</div>}
  </Card>;
}

// ===== Coordinator Dashboard =====
function CoordinatorDashboard({ cid }) {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/cases/${cid}/dashboard`).then(r => setD(r.data)); }, [cid]);
  if (!d) return <Card><p className="text-[#4A5553]">جاري التحميل...</p></Card>;
  const p = d.profile_summary;
  return <div className="space-y-5" data-testid="coordinator-dashboard">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Stat label="أفراد الفريق" value={d.team.length} icon={Users} color="text-[#2F5233]"/>
      <Stat label="أهداف نشطة" value={d.active_goals_count} icon={Target} color="text-[#769FCD]"/>
      <Stat label="ملاحظات حديثة" value={d.recent_notes.length} icon={Stethoscope} color="text-[#E8A317]"/>
      <Stat label="متابعات معلّقة" value={d.pending_followups.length} icon={AlertCircle} color="text-[#D9534F]"/>
    </div>

    <Card>
      <h3 className="text-lg font-semibold mb-3 font-heading">ملخص ملف الحالة</h3>
      <div className="grid md:grid-cols-3 gap-3 text-sm">
        <Field label="الاسم" value={p.name}/>
        <Field label="العمر" value={`${p.age} سنة`}/>
        <Field label="الصف" value={p.grade_class || "—"}/>
        <Field label="مستوى التواصل" value={COMM_LEVELS.find(x=>x.v===p.communication_level)?.l || "—"}/>
        <Field label="مستوى الدعم" value={SUPPORT_LEVELS.find(x=>x.v===p.support_level)?.l || "—"}/>
        <Field label="إطار الدمج" value={INCLUSION.find(x=>x.v===p.inclusion_setting)?.l || "—"}/>
      </div>
    </Card>

    <div className="grid md:grid-cols-2 gap-5">
      <Card>
        <h3 className="text-lg font-semibold mb-3 font-heading flex items-center gap-2"><Users size={18}/>الفريق المعيّن</h3>
        {d.team.length === 0 ? <p className="text-sm text-[#4A5553]">لم يُعيّن متخصصون بعد</p> :
          <div className="space-y-2">{d.team.map(m => <div key={m.id} className="flex justify-between items-center p-2 bg-[#F9F8F6] rounded">
            <span className="text-sm font-medium">{m.name}</span>
            <span className="text-xs text-[#4A5553]">{m.role_label}</span></div>)}</div>}
      </Card>
      <Card>
        <h3 className="text-lg font-semibold mb-3 font-heading flex items-center gap-2"><Target size={18}/>الأهداف النشطة</h3>
        {d.active_goals.length === 0 ? <p className="text-sm text-[#4A5553]">لا توجد أهداف نشطة</p> :
          <div className="space-y-2">{d.active_goals.slice(0,8).map(g => <div key={g.id} className="p-2 bg-[#F9F8F6] rounded">
            <p className="text-sm font-medium">{g.title}</p>
            <p className="text-xs text-[#4A5553] mt-0.5">{g.domain} {g.responsible && `· ${g.responsible}`}</p></div>)}</div>}
      </Card>
    </div>

    <Card>
      <h3 className="text-lg font-semibold mb-3 font-heading flex items-center gap-2"><Stethoscope size={18}/>آخر الملاحظات</h3>
      {d.recent_notes.length === 0 ? <p className="text-sm text-[#4A5553]">لا توجد ملاحظات</p> :
        <div className="space-y-2">{d.recent_notes.slice(0,5).map(n => <div key={n.id} className="p-3 border border-[#E5E2DB] rounded-lg">
          <div className="flex justify-between items-start text-xs text-[#4A5553]"><span>{n.author} · {n.role_label}</span><span>{fmtDate(n.ts)}</span></div>
          <p className="text-sm mt-1">{n.observation}</p></div>)}</div>}
    </Card>

    {d.pending_followups.length > 0 && <Card className="border-[#E8A317]/30 bg-[#E8A317]/5">
      <h3 className="text-lg font-semibold mb-3 font-heading flex items-center gap-2"><AlertCircle size={18} className="text-[#E8A317]"/>متابعات معلّقة</h3>
      <div className="space-y-2">{d.pending_followups.map(n => <div key={n.id} className="p-3 bg-white rounded-lg border border-[#E5E2DB]">
        <p className="text-sm text-[#4A5553]">{n.author} ({n.role_label}) · {fmtDate(n.ts)}</p>
        {n.concern && <p className="text-sm mt-1"><strong className="text-[#E8A317]">عائق:</strong> {n.concern}</p>}
        {n.recommendation && <p className="text-sm mt-1"><strong className="text-[#527598]">توصية:</strong> {n.recommendation}</p>}
      </div>)}</div>
    </Card>}
  </div>;
}

function Stat({ label, value, icon: Icon, color }) {
  return <Card className="text-center"><Icon className={`mx-auto mb-2 ${color}`} size={24}/>
    <p className={`text-3xl font-bold ${color}`}>{value}</p>
    <p className="text-xs text-[#4A5553] mt-1">{label}</p></Card>;
}

// ===== Reusable Goal Achievement progress bar =====
function GoalAchievementBar({ score, linkedCount, colorCls, barCls, testid }) {
  const display = score == null ? "—" : `${score}%`;
  const label = score == null
    ? "لم تكتمل بيانات Rubric أو الأدلة بعد"
    : score >= 70 ? "متحقق بدرجة قوية"
    : score >= 40 ? "في تقدم نحو التحقق"
    : "بحاجة لتدخل إضافي";
  return <div className="space-y-1" data-testid={testid}>
    <div className="flex justify-between items-center text-[11px]">
      <span className="text-[#4A5553]">درجة تحقق الهدف · {linkedCount} تقييم</span>
      <span className={`px-1.5 py-0.5 rounded font-mono ${colorCls}`}>{display}</span>
    </div>
    <div className="w-full h-1.5 bg-[#F0EFEB] rounded-full overflow-hidden">
      <div className={`h-full ${barCls} transition-all`} style={{ width: `${Math.max(2, score || 0)}%` }}/>
    </div>
    <p className="text-[10px] text-[#4A5553]">{label}</p>
  </div>;
}
